export interface PictureBankGroupItem {
	groupID: string;
}

export interface PictureBankSizeProfile {
	profileID: string;
	profileName: string;
	/** Output size — what the add-in inserts into the document. */
	width: number;
	height: number;
	isCrop: boolean;
	isBackgroundImage: boolean;
	/** Thumb size for the add-in picture grid. Backend auto-calculates if left 0 (fits within 275×400). */
	thumbSmallWidth: number;
	thumbSmallHeight: number;
	/** Hover/big preview size. Backend auto-calculates if left 0 (fits within 300×500). */
	thumbLargeWidth: number;
	thumbLargeHeight: number;
	/** Optional platform filter. Empty = all platforms. */
	platforms?: string[];
	/** Number of images that currently match this profile. Returned by getDefs. */
	imageCount?: number;
}

/** Reference to a size profile by ID (name kept for display/back-compat). */
export interface PictureBankProfileRef {
	profileID: string;
	profileName: string;
}

export interface PictureBankCropDef {
	profileID: string;
	cropX: number;
	cropY: number;
	cropWidth: number;
	cropHeight: number;
	isAuto: boolean;
}

/** Per-profile flip override. Applied on top of the image-level transform for this profile. */
export interface PictureBankProfileTransform {
	profileID: string;
	flipHorizontal: boolean;
	flipVertical: boolean;
}

export interface PictureBankLibrary {
	libraryID: string;
	libraryName: string;
	libraryDescription: string;
	libraryGroups?: PictureBankGroupItem[];
	platforms?: string[];
	/** false = library exposes no regular size profiles. null/true = allowed. Configured via Kudu until an admin UI exists. */
	forRegular?: boolean | null;
	/** false = library exposes no background profiles. null/true = allowed. Configured via Kudu until an admin UI exists. */
	forBackground?: boolean | null;
}

export interface PictureBankCategory {
	categoryID: string;
	categoryName: string;
	categoryDescription: string;
	categoryGroups?: PictureBankGroupItem[];
	color?: string;
}

export interface PictureBankCategoryRef {
	categoryID: string;
	categoryName: string;
}

export interface PictureBankLibraryRef {
	libraryID: string;
	libraryName: string;
}

export interface PictureBankImageMeta {
	imageID: string;
	imageName: string;
	imageDescription: string;
	originalWidth: number;
	originalHeight: number;
	fileSize: string;
	fileSizeNum: number;
	imgType: string;
	uploadDate: string;
	publishDate: string;
	isPublished: boolean;
	imageGroups?: PictureBankGroupItem[];
	crops: PictureBankCropDef[];
	platforms?: string[];
	/** @deprecated Legacy name-based allowlist. Read for back-compat only; never send back — use allowedSizeRefs/allowedBackgroundRefs. */
	allowedProfiles?: string[];
	/** Allowlist of regular size profiles (by ID). Empty = all regular profiles (when forRegular). */
	allowedSizeRefs?: PictureBankProfileRef[];
	/** Allowlist of background profiles (by ID). Empty = all background profiles (when forBackground). */
	allowedBackgroundRefs?: PictureBankProfileRef[];
	/** Category IDs the image belongs to (multi). */
	categories: string[];
	/** Library IDs the image belongs to (multi; must have at least one). */
	libraries: string[];
	rotation?: number;
	flipHorizontal?: boolean;
	flipVertical?: boolean;
	profileTransforms?: PictureBankProfileTransform[];
	/** Profile IDs that have a custom override image uploaded via postProfileImage. */
	profileImages?: string[];
	/** @deprecated Legacy type flag. Read as fallback for forRegular/forBackground; not shown in new UI. */
	isBackgroundImage?: boolean | null;
	/** Available for regular size profiles. null = treated as true. */
	forRegular?: boolean | null;
	/** Available for background profiles. null = treated as false (unless legacy isBackgroundImage). */
	forBackground?: boolean | null;
}

export interface PictureBankListItem {
	imageID: string;
	imageName: string;
	imageDescription: string;
	categories: PictureBankCategoryRef[];
	libraries: PictureBankLibraryRef[];
	originalWidth: number;
	originalHeight: number;
	fileSize: string;
	fileSizeNum: number;
	imgType: string;
	uploadDate: string;
	publishDate: string;
	isPublished: boolean;
	imageGroups?: PictureBankGroupItem[];
	crops: PictureBankCropDef[];
	platforms?: string[];
	/** @deprecated Legacy name-based allowlist. Read for back-compat only. */
	allowedProfiles?: string[];
	/** Allowlist of regular size profiles (by ID). Empty = all regular profiles (when forRegular). */
	allowedSizeRefs?: PictureBankProfileRef[];
	/** Allowlist of background profiles (by ID). Empty = all background profiles (when forBackground). */
	allowedBackgroundRefs?: PictureBankProfileRef[];
	previewBase64: string;
	rotation?: number;
	flipHorizontal?: boolean;
	flipVertical?: boolean;
	profileTransforms?: PictureBankProfileTransform[];
	profileImages?: string[];
	/** @deprecated Legacy type flag. Read as fallback only. */
	isBackgroundImage?: boolean | null;
	/** Available for regular size profiles. null = treated as true. */
	forRegular?: boolean | null;
	/** Available for background profiles. null = treated as false (unless legacy isBackgroundImage). */
	forBackground?: boolean | null;
}

export interface PictureBankPagedImages {
	totalCount: number;
	items: PictureBankListItem[];
}

export interface PictureBankProfileThumb {
	imageID: string;
	imageName: string;
	categories: PictureBankCategoryRef[];
	libraries: PictureBankLibraryRef[];
	thumbSmallBase64: string;
	thumbSmallWidth: number;
	thumbSmallHeight: number;
	thumbLargeWidth: number;
	thumbLargeHeight: number;
}

export interface PictureBankDefs {
	sizeProfiles: PictureBankSizeProfile[];
	categories: PictureBankCategory[];
	libraries: PictureBankLibrary[];
	/** Number of images still using the legacy name-based allowedProfiles that need migrating to profile-ID refs. */
	migratePending?: number;
}

export type PictureBankVersion = 'TEST' | 'PUB' | 'BACKUP';

export const PICTURE_BANK_PLATFORMS = ['Word', 'Excel', 'PowerPoint'] as const;
export type PictureBankPlatform = typeof PICTURE_BANK_PLATFORMS[number];

export const emptyPictureBankDefs: PictureBankDefs = {
	sizeProfiles: [],
	categories: [],
	libraries: [],
};
